import React, { useState, useEffect, useCallback } from 'react';

import { Checkbox, FormGroup, FormControl, FormHelperText, FormControlLabel } from '@mui/material';

import ElementLabel from './ElementLabel';
import { ElementProps } from '../../../@types/forms/types';

//-----------------------------------------------------------

type CheckboxElementProps = {
  el: ElementProps;
  pageNumberScale: number;
  isFormFillerPage: boolean;
  elementContentValue?: string[] | undefined;
  handleCheckboxChange?: (el: ElementProps, index: number) => void;
  collaboratorColor?: string;
  disabled?: boolean;
  errorMessage?: string;
};

const CheckboxElement: React.FC<CheckboxElementProps> = ({
  el,
  pageNumberScale,
  elementContentValue,
  isFormFillerPage,
  handleCheckboxChange,
  collaboratorColor,
  disabled = false,
  errorMessage,
}) => {
  const [value, setValue] = useState<string[]>([]);

  const handleCheckboxValueValidation = useCallback(
    (option: string) => {
      const optionIndex = value.findIndex((val) => val === option);

      if (optionIndex === -1) {
        setValue([...value, option]);
      } else {
        const newValue = [...value];
        newValue.splice(optionIndex, 1);

        setValue([...newValue]);
      }
    },
    [value]
  );

  useEffect(() => {
    if (elementContentValue) {
      setValue(elementContentValue);
    } else {
      setValue([]);
    }
  }, [elementContentValue]);

  const createClickHandler = useCallback(
    (index: number, optionLabel: string) => () => {
      if (handleCheckboxChange) {
        handleCheckboxChange(el, index);
        handleCheckboxValueValidation(optionLabel);
      }
    },
    [handleCheckboxChange, el, handleCheckboxValueValidation]
  );

  return (
    <>
      {el.formGroupOptions && el.formGroupOptions?.length > 0 && (
        <FormControl
          component="fieldset"
          variant="standard"
          error={!!errorMessage}
          sx={{
            backgroundColor: collaboratorColor,
            borderRadius: 1,
            px: 1,
            width: '100%',
          }}
          id={el.id}
        >
          <FormGroup>
            {el.formGroupOptions.map((option, index) => (
              <FormControlLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: `${Math.round(el.fontSize * pageNumberScale)}px`,
                  // m: 0,
                  // p: 0
                }}
                key={`${el.id}-${index + 1}`}
                control={
                  <Checkbox
                    sx={{
                      fontSize: `${el.fontSize * pageNumberScale}`,
                      m: 0,
                      p: 0,
                      pr: 1,
                    }}
                    checked={value.includes(option.label)}
                    name={option.label}
                    disabled={disabled}
                    onClick={createClickHandler(index, option.label)}
                  />
                }
                label={
                  <ElementLabel
                    el={el}
                    label={option.label}
                    pageNumberScale={pageNumberScale}
                    asChildEl
                  />
                }
              />
            ))}
          </FormGroup>
          {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
      )}

      {(!el.formGroupOptions || el.formGroupOptions?.length === 0) && (
        <ElementLabel
          el={el}
          label={el.label}
          pageNumberScale={pageNumberScale}
          collaboratorColor={collaboratorColor}
        />
      )}
    </>
  );
};

export default CheckboxElement;
